import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'

declare const microsoftTeams: any

type PanelStatus = 'LOADING' | 'NO_CALL' | 'NOT_ENABLED' | 'SCHEDULED' | 'CONNECTING' | 'RECORDING' | 'PAUSED' | 'HANGING_UP' | 'ENDED'

type CallInfo = {
  callId: number
  accessKey: string
  status: PanelStatus
  callUrl: string | null
  startedAt: string | null
  scheduledEndAt: string | null
}

@Component({
  selector: 'app-teams-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './teams-panel.component.html',
  styleUrl: './teams-panel.component.scss',
})
export class TeamsPanelComponent implements OnInit, OnDestroy {
  panelStatus: PanelStatus = 'LOADING'
  callInfo: CallInfo | null = null
  elapsedTime = '00:00'
  remainingTime: string | null = null
  isOvertime = false
  actionInProgress = false
  errorMessage: string | null = null

  private timerInterval: ReturnType<typeof setInterval> | null = null
  private pollInterval: ReturnType<typeof setInterval> | null = null
  private startTime = Date.now()

  private readonly apiBaseUrl = '/api/bot'
  private ssoToken: string | null = null

  private mapSessionStatus(status: string): PanelStatus {
    if (status === 'CONNECTING' || status === 'WAITING_TO_BE_ADMITTED' || status === 'QUEUED') {
      return 'CONNECTING'
    }
    if (status === 'PAUSED') {
      return 'PAUSED'
    }
    if (status === 'HANGING_UP') {
      return 'HANGING_UP'
    }
    if (status === 'ENDED' || status === 'ERRORED') {
      return 'ENDED'
    }
    return 'RECORDING'
  }
  private readonly http = inject(HttpClient)
  private readonly translate = inject(TranslateService)

  constructor(
    private readonly ngZone: NgZone,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadTeamsSdk()
  }

  ngOnDestroy() {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval)
    }
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval)
    }
  }

  isPausing = false
  isResuming = false
  isDisconnecting = false

  async pause() {
    if (this.callInfo === null || this.actionInProgress) {
      return
    }
    this.actionInProgress = true
    this.isPausing = true
    this.errorMessage = null

    try {
      await this.panelRequest('POST', `${this.callInfo.callId}/pause`)
    } catch {
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_PAUSE')
      this.actionInProgress = false
      this.isPausing = false
    }
  }

  async resume() {
    if (this.callInfo === null || this.actionInProgress) {
      return
    }
    this.actionInProgress = true
    this.isResuming = true
    this.errorMessage = null

    try {
      await this.panelRequest('POST', `${this.callInfo.callId}/resume`)
    } catch {
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_RESUME')
      this.actionInProgress = false
      this.isResuming = false
    }
  }

  showDisconnectConfirm = false

  requestDisconnect() {
    this.showDisconnectConfirm = true
  }

  cancelDisconnect() {
    this.showDisconnectConfirm = false
  }

  async confirmDisconnect() {
    this.showDisconnectConfirm = false
    if (this.callInfo === null || this.actionInProgress) {
      return
    }
    this.actionInProgress = true
    this.isDisconnecting = true
    this.errorMessage = null

    try {
      await this.panelRequest('POST', `${this.callInfo.callId}/hang-up`)
    } catch {
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_DISCONNECT')
      this.actionInProgress = false
      this.isDisconnecting = false
    }
  }

  private loadTeamsSdk() {
    if (typeof microsoftTeams !== 'undefined') {
      this.initializeTeams()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://res.cdn.office.net/teams-js/2.24.0/js/MicrosoftTeams.min.js'
    script.onload = () => {
      this.initializeTeams()
    }
    script.onerror = () => {
      this.ngZone.run(() => {
        this.panelStatus = 'NO_CALL'
        this.changeDetector.detectChanges()
      })
    }
    document.head.appendChild(script)
  }

  private async initializeTeams() {
    try {
      const initTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Teams SDK timeout')), 5000))
      await Promise.race([microsoftTeams.app.initialize(), initTimeout])

      try {
        this.ssoToken = await microsoftTeams.authentication.getAuthToken()
      } catch (error) {
        console.warn('Failed to get Teams SSO token', error)
      }

      const context = await microsoftTeams.app.getContext()
      const threadId = context?.chat?.id ?? null

      if (threadId === null) {
        this.ngZone.run(() => {
          this.panelStatus = 'NO_CALL'
          this.changeDetector.detectChanges()
        })
        return
      }

      await this.resolveCall(threadId)
    } catch {
      this.ngZone.run(() => {
        this.panelStatus = 'NO_CALL'
        this.changeDetector.detectChanges()
      })
    }
  }

  private async resolveCall(threadId: string) {
    try {
      const headers: Record<string, string> = {}
      if (this.ssoToken !== null) {
        headers['Authorization'] = `Bearer ${this.ssoToken}`
      }
      const data = await firstValueFrom(this.http.get<any>(`${this.apiBaseUrl}/by-thread/${encodeURIComponent(threadId)}`, { headers }))

      this.ngZone.run(() => {
        if (data.status === 'READY' || data.status === 'INITIALIZING') {
          this.callInfo = {
            callId: data.callId,
            accessKey: data.accessKey,
            status: 'CONNECTING',
            callUrl: null,
            startedAt: null,
            scheduledEndAt: null,
          }
          this.panelStatus = 'CONNECTING'
          this.changeDetector.detectChanges()

          this.pollStatus()
          this.startTimer()
          this.startPolling()
        } else if (data.status === 'NOT_ENABLED') {
          this.panelStatus = 'NOT_ENABLED'
          this.changeDetector.detectChanges()
        } else if (data.status === 'SCHEDULED') {
          this.panelStatus = 'SCHEDULED'
          this.changeDetector.detectChanges()
          setTimeout(() => this.resolveCall(threadId), 5000)
        } else {
          this.panelStatus = 'NO_CALL'
          this.changeDetector.detectChanges()
          setTimeout(() => this.resolveCall(threadId), 5000)
        }
      })
    } catch {
      this.ngZone.run(() => {
        this.panelStatus = 'NO_CALL'
        this.changeDetector.detectChanges()
      })
      setTimeout(() => this.resolveCall(threadId), 5000)
    }
  }

  private formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const seconds = (totalSeconds % 60).toString().padStart(2, '0')
    return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`
  }

  private formatMinutes(totalSeconds: number): string {
    const minutes = Math.ceil(totalSeconds / 60)
    return `${minutes} min`
  }

  private startTimer() {
    this.ngZone.runOutsideAngular(() => {
      this.timerInterval = setInterval(() => {
        const now = Date.now()
        const elapsed = Math.floor((now - this.startTime) / 1000)
        this.elapsedTime = this.formatDuration(elapsed)

        if (this.callInfo !== null && this.callInfo.scheduledEndAt !== null) {
          const endTime = new Date(this.callInfo.scheduledEndAt).getTime()
          const remaining = Math.max(0, Math.floor((endTime - now) / 1000))
          this.isOvertime = remaining === 0
          this.remainingTime = remaining > 0 ? this.formatMinutes(remaining) : this.translate.instant('TEAMS_PANEL.OVERTIME')
        } else {
          this.remainingTime = null
          this.isOvertime = false
        }

        this.changeDetector.detectChanges()
      }, 1000)
    })
  }

  private startPolling() {
    this.ngZone.runOutsideAngular(() => {
      this.pollInterval = setInterval(() => {
        this.pollStatus()
      }, 2000)
    })
  }

  private stopPolling() {
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  private async pollStatus() {
    if (this.callInfo === null) {
      return
    }

    try {
      const data = await firstValueFrom(
        this.http.get<any>(`${this.apiBaseUrl}/${this.callInfo.callId}/status?accessKey=${encodeURIComponent(this.callInfo.accessKey)}`)
      )

      this.ngZone.run(() => {
        const mappedStatus = this.mapSessionStatus(data.status)
        if (
          (this.isPausing && mappedStatus === 'PAUSED') ||
          (this.isResuming && mappedStatus === 'RECORDING') ||
          (this.isDisconnecting && mappedStatus === 'ENDED')
        ) {
          this.actionInProgress = false
          this.isPausing = false
          this.isResuming = false
          this.isDisconnecting = false
        }
        if (!this.actionInProgress) {
          this.panelStatus = mappedStatus
        }
        const callUrl = data.callUrl ?? null
        if (callUrl !== null) {
          this.callInfo!.callUrl = callUrl
        }
        const startedAt = data.startedAt ?? null
        if (startedAt !== null) {
          this.startTime = new Date(startedAt).getTime()
        }
        const scheduledEndAt = data.scheduledEndAt ?? null
        if (scheduledEndAt !== null) {
          this.callInfo!.scheduledEndAt = scheduledEndAt
        }
        if (mappedStatus === 'ENDED') {
          this.actionInProgress = false
          this.isPausing = false
          this.isResuming = false
          this.isDisconnecting = false
          this.panelStatus = 'ENDED'
          this.stopPolling()
          if (this.timerInterval !== null) {
            clearInterval(this.timerInterval)
            this.timerInterval = null
          }
        }
        this.changeDetector.detectChanges()
      })
    } catch {
      // Silently ignore poll failures
    }
  }

  private async panelRequest(method: string, path: string) {
    if (this.callInfo === null) {
      throw new Error('No call info')
    }

    const url = `${this.apiBaseUrl}/${path}?accessKey=${encodeURIComponent(this.callInfo.accessKey)}`
    await firstValueFrom(method === 'POST' ? this.http.post(url, {}) : this.http.get(url))
  }
}
